import UndoIcon from "@mui/icons-material/Undo";
import { Box, Button, IconButton, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import type { DebugFlagKind, DebugFlagRow } from "@/components/Workspace/debugFlagRows";

const MONOSPACE = "ui-monospace, SFMono-Regular, Menlo, monospace";
const DEBUG_MENU_HEADING_ID = "debug-menu-heading";

/** Longest read-only value shown inline; longer values are cut and shown whole in a tooltip. */
const MAX_INLINE_VALUE_LENGTH = 80;

/** Hairline used for the panel frame and between rows. */
const RULE = "1px solid var(--color-zinc-800)";

/** The groups the panel lists flags under, in display order. Toggles and numbers sit in a multi-column grid. */
const FLAG_GROUPS: { kind: DebugFlagKind; title: string; columns: boolean }[] = [
  { kind: "boolean", title: "Toggles", columns: true },
  { kind: "number", title: "Numbers", columns: true },
  { kind: "readonly", title: "Values", columns: false },
];

type Props = {
  rows: DebugFlagRow[];
  filter: string;
  onFilterChange: (filter: string) => void;
  onChange: (key: string, value: boolean | number) => void;
  onReset: (key: string) => void;
  onResetAll: () => void;
};

/**
 * The profile page's debug menu: a framed panel with a header bar, a filter, and every flag grouped by type, each
 * with an editor matched to its value and its override state. A pure view; the caller wires it to `AppContext`.
 */
export default function DebugFlagsPanel({ rows, filter, onFilterChange, onChange, onReset, onResetAll }: Props) {
  const overriddenCount = rows.filter((row) => row.overridden).length;

  return (
    <Box
      component="section"
      aria-labelledby={DEBUG_MENU_HEADING_ID}
      sx={{ border: RULE, borderRadius: 2, bgcolor: "var(--color-zinc-950)", overflow: "hidden" }}
    >
      {/* Header bar: title and counts on the left, the bulk reset on the right. */}
      <Stack direction="row" sx={{ alignItems: "center", gap: 2, px: 3, py: 2, borderBottom: RULE }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={DEBUG_MENU_HEADING_ID} component="h2" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
            Debug menu
          </Typography>
          <Box sx={{ fontSize: "13px", color: "neutral.main" }}>
            {rows.length} flags · {overriddenCount} overridden · Overrides apply to this tab only and last until you
            reload.
          </Box>
        </Box>
        <Button
          variant="text"
          size="small"
          disabled={overriddenCount === 0}
          onClick={onResetAll}
          startIcon={<UndoIcon fontSize="small" />}
          sx={{
            width: "auto",
            flex: "none",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:disabled": { background: "transparent" },
          }}
        >
          Reset all overrides
        </Button>
      </Stack>

      <Box sx={{ px: 3, py: 2, borderBottom: RULE }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter by flag key"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value)}
          slotProps={{ htmlInput: { "aria-label": "Filter flags" } }}
        />
      </Box>

      {/* No inner scroll: the panel grows with its flags and the page scrolls. */}
      <Stack sx={{ px: 3, py: 2, gap: 3 }}>
        {rows.length === 0 && <Box sx={{ color: "neutral.main", fontSize: "14px" }}>No flags match “{filter}”.</Box>}
        {FLAG_GROUPS.map((group) => {
          const groupRows = rows.filter((row) => row.kind === group.kind);
          if (groupRows.length === 0) return null;
          return (
            <Box key={group.kind}>
              <Typography
                component="h3"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "neutral.main",
                  mb: 1,
                }}
              >
                {group.title} ({groupRows.length})
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: group.columns ? "repeat(auto-fill, minmax(300px, 1fr))" : "1fr",
                  columnGap: 4,
                }}
              >
                {groupRows.map((row) => (
                  <DebugFlagRowView key={row.key} row={row} onChange={onChange} onReset={onReset} />
                ))}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function DebugFlagRowView({
  row,
  onChange,
  onReset,
}: {
  row: DebugFlagRow;
  onChange: (key: string, value: boolean | number) => void;
  onReset: (key: string) => void;
}) {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        gap: 1,
        minHeight: "48px",
        py: 0.5,
        pl: 1.5,
        borderBottom: RULE,
        // An overridden row carries a brand accent on its left edge.
        borderLeft: row.overridden ? "2px solid var(--color-brand-primary-400)" : "2px solid transparent",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          title={row.key}
          sx={{
            fontFamily: MONOSPACE,
            fontSize: "13px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.key}
        </Box>
        {row.overridden && (
          <Box sx={{ fontSize: "12px", color: "var(--color-brand-primary-300)" }}>
            overridden ·{" "}
            <Box component="span" sx={{ fontFamily: MONOSPACE }}>
              {row.launchDarklyValue === undefined
                ? "not in LaunchDarkly"
                : `LD: ${formatValue(row.launchDarklyValue)}`}
            </Box>
          </Box>
        )}
      </Box>

      {row.overridden && (
        <IconButton size="small" aria-label={`Reset ${row.key}`} onClick={() => onReset(row.key)}>
          <UndoIcon fontSize="small" />
        </IconButton>
      )}

      <FlagEditor row={row} onChange={onChange} />
    </Stack>
  );
}

/** The editor for one flag: a switch, a number input, or the value as read-only text. */
function FlagEditor({
  row,
  onChange,
}: {
  row: DebugFlagRow;
  onChange: (key: string, value: boolean | number) => void;
}) {
  switch (row.kind) {
    case "boolean":
      return (
        <Switch
          checked={row.value === true}
          onChange={(event) => onChange(row.key, event.target.checked)}
          slotProps={{ input: { "aria-label": row.key } }}
        />
      );
    case "number":
      return <NumberFlagEditor flagKey={row.key} value={Number(row.value)} onChange={onChange} />;
    case "readonly": {
      const text = formatValue(row.value);
      const clipped = text.length > MAX_INLINE_VALUE_LENGTH;
      return (
        <Tooltip title={clipped ? text : ""} placement="left">
          <Box
            sx={{
              fontFamily: MONOSPACE,
              fontSize: "12px",
              color: "var(--color-zinc-300)",
              maxWidth: "60%",
              textAlign: "right",
              overflowWrap: "anywhere",
            }}
          >
            {clipped ? `${text.slice(0, MAX_INLINE_VALUE_LENGTH)}…` : text}
          </Box>
        </Tooltip>
      );
    }
  }
}

/**
 * A number input that keeps its own draft text, so the field can be emptied mid-edit. It reports only valid,
 * non-negative numbers, and goes back to the flag's value on blur when the draft is not one.
 */
function NumberFlagEditor({
  flagKey,
  value,
  onChange,
}: {
  flagKey: string;
  value: number;
  onChange: (key: string, value: boolean | number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  // Follow the flag when it changes from outside the field: a reset or a LaunchDarkly update.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  return (
    <TextField
      type="number"
      size="small"
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        const parsed = Number(event.target.value);
        // Every numeric flag is a count, limit or duration, so only non-negative numbers are reported.
        if (event.target.value.trim() !== "" && !Number.isNaN(parsed) && parsed >= 0) onChange(flagKey, parsed);
      }}
      onBlur={() => setDraft(String(value))}
      slotProps={{ htmlInput: { min: 0, "aria-label": flagKey } }}
      sx={{ width: "110px", flex: "none" }}
    />
  );
}

function formatValue(value: unknown): string {
  return typeof value === "string" ? value : String(JSON.stringify(value));
}
